import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Shield, Zap, Handshake, ArrowRight, Target, Eye, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroImages, values } from '../data/mockData';

const About = () => {
  return (
    <div className="min-h-screen pt-28 md:pt-36">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImages.about}
            alt="Our Team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/60"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              About <span className="text-red-500">FinVestra</span> Consulting
            </h1>
            <p className="text-xl text-gray-300">
              Your trusted partner for strategic excellence and business transformation across the UAE.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Who We Are
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                FinVestra Consulting FZ-LLC is a UAE-based consulting and advisory firm established to deliver comprehensive business solutions with a unique execution-focused approach. We are not just consultants—we are strategic partners committed to turning vision into reality.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Operating from Ras Al Khaimah Free Zone, we serve clients across the United Arab Emirates, providing expert guidance in Finance, Investment, Strategy, Management Consulting, Recruitment, ICV Advisory, and Business Setup. Our team combines deep local market knowledge with international best practices to deliver exceptional value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            <Card className="border-t-4 border-t-red-600">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                  To be the most trusted and results-driven business consulting partner in the UAE, known for transforming strategies into sustainable competitive advantages.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-red-600">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                  To deliver exceptional advisory and execution services that drive measurable business growth, operational excellence, and long-term value for our clients.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-red-600">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Purpose</h3>
                <p className="text-gray-600 leading-relaxed">
                  To empower UAE businesses with strategic insights and hands-on execution support, enabling them to navigate complexity and achieve sustained success.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Core Values */}
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10 text-center">
              Our Core Values
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((value, index) => {
                const IconComponent = { Star, Shield, Zap, Handshake }[value.icon] || Star;
                
                return (
                  <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                          <IconComponent className="w-6 h-6 text-red-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{value.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Advisory Plus Execution Positioning */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Advisory <span className="text-red-600">+ Execution</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                What sets FinVestra apart is our commitment to execution. We don't just provide recommendations and walk away—we work alongside you to implement strategies, manage projects, and deliver measurable outcomes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-l-red-600">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Traditional Consulting</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                    Diagnosis and recommendations
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                    Strategic frameworks
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                    Handover and exit
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-l-red-600">
                <h3 className="text-xl font-bold text-gray-900 mb-3">FinVestra Approach</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                    Diagnosis and strategy development
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                    Hands-on implementation support
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                    Long-term partnership and optimization
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Partner With Us for Sustainable Growth
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Experience the FinVestra difference—strategic excellence backed by execution mastery.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 h-12">
              Schedule a Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
